import * as _ from 'underscore';
import Axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from 'axios';
import {ServiceLocator} from '../app/ServiceLocator';
import { IResponse, IResult } from './serverApi/types/IResponse';
import {ErrorReason} from '../sessionManagement/SessionManagementService';
import {IPodRedirectionError} from '../ts/ServerApi/types/IPodRedirectionError';
import {CommonErrorCode} from '../ts/helpers/ErrorCodes';
import {maybeHandleSearchServerDown} from './HandleSearchServerDown';
import {getAuthenticationServiceInstance} from '../authentication/services/AuthenticationService';
import { IError } from './serverApi/types/IError';

let activeRequestCount = 0;
let notifyWhenNoActiveRequestFunction: Function;
let timeoutHandle: any;
function notifyWhenNoActiveRequest(callback: Function){
  notifyWhenNoActiveRequestFunction = _.debounce(callback, 500);
  timeoutHandle = setTimeout(() => {if (activeRequestCount === 0) callback();}, 1000);
}

// TODO: move the error handling, etc utility functions one layer up. They don't belong to core AxiosHttpClient
function shouldRedirectToOtherPod(errorCode: number, destinationPodUrl: string): boolean {
  return errorCode && (errorCode === CommonErrorCode.RedirectToOtherPod) && (destinationPodUrl !== null);
}

function getFirstErrorFromResponse(response: AxiosResponse<IResponse<any>>): IError {
  return response && response.data && response.data.result &&
    response.data.result.errors && response.data.result.errors[0];
}

function getCodeFromError(error: IError): number {
  return error && error.code;
}

function getDestinationPodFromError(error: IError): string {
  const podRedirectionError = <IPodRedirectionError>error;
  return podRedirectionError && podRedirectionError.destinationPodUrl;
}

function updateRequestCountAndTriggerDigest() {
  try {
    ServiceLocator.get<ng.IRootScopeService>('$rootScope').$applyAsync();
  } catch {
    // ignore the error since it only happens in unit tests if $rootscope is not injected
  }
  activeRequestCount--;
  if (activeRequestCount === 0) {
    clearTimeout(timeoutHandle);
    notifyWhenNoActiveRequestFunction?.();
  }
}

function makeAxiosRequest<TResult extends IResult>(requestConfig: AxiosRequestConfig): Promise<TResult> {
  activeRequestCount++;
  return Axios(requestConfig)
    .then(
      (successResponse: AxiosResponse<IResponse<TResult>>) => {
        updateRequestCountAndTriggerDigest();
        const error = getFirstErrorFromResponse(successResponse);
        const code: number = getCodeFromError(error);
        if (maybeHandleSearchServerDown(code)) {
          return Promise.reject(code);
        }
        const location: string = getDestinationPodFromError(error);
        if (shouldRedirectToOtherPod(code, location)) {
          return Promise.reject(error);
        }
        return successResponse.data && !_.isUndefined(successResponse.data.result)
          ? successResponse.data.result
          : null;
      },
      (errorObject: AxiosError<IResponse<TResult>>) => {
        updateRequestCountAndTriggerDigest();
        // TFSID 1067441: When refreshing the page with requests still pending, the pending requests are aborted
        // by some browsers (Safari in particular). When Safari aborts these pending requests, it triggers the
        // 'error' status for the XHR, which triggers this code. The response for the aborted requests has empty
        // headers and an empty body. So, when a request is aborted in this manner, return the "EmptyResponse"
        // so this case can be handled.
        // Update (08/19/21) - error.response can be empty too as shown in TFSID:1300678
        if (_.isEmpty(errorObject?.response?.headers)) {
          return Promise.reject(ErrorReason.EmptyResponse);
        }
        const reason: number = getCodeFromError(getFirstErrorFromResponse(errorObject.response));
        // TODO: remove search specific error handling one level up. Cocern for AxiosHttpClient should only be generic error handling
        if (maybeHandleSearchServerDown(reason)) {
          return Promise.reject(reason);
        }
        if (errorObject.response.status === 401) {
          getAuthenticationServiceInstance().handle401(reason);
        } else if (errorObject.response.status === 429) {
          return errorObject.response.data.result;
        }
        return Promise.reject(reason || ErrorReason.Unknown);
      }
    );
}

export default {
  getDestinationPodFromError,
  makeAxiosRequest,
  notifyWhenNoActiveRequest
};
